import { useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to manage native push notifications via Capacitor.
 * Only activates on native platforms (iOS/Android).
 * On web, this is a no-op.
 */
export function usePushNotifications(token: string | null) {
  const registered = useRef(false);

  const sendTokenToBackend = useCallback(async (pushToken: string) => {
    if (!token) return;
    try {
      await fetch(`${API}/api/push/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          push_token: pushToken,
          platform: Capacitor.getPlatform(),
        }),
      });
    } catch (err) {
      console.error('Failed to register push token with backend:', err);
    }
  }, [token]);

  const initPush = useCallback(async () => {
    if (!Capacitor.isNativePlatform() || registered.current) return;
    try {
      let permStatus;
      try {
        permStatus = await PushNotifications.checkPermissions();
      } catch (permErr) {
        console.warn('Push permission check failed, skipping push setup:', permErr);
        return;
      }
      if (permStatus.receive === 'prompt') {
        try {
          permStatus = await PushNotifications.requestPermissions();
        } catch (reqErr) {
          console.warn('Push permission request failed, skipping push setup:', reqErr);
          return;
        }
      }
      if (permStatus.receive !== 'granted') {
        console.warn('Push notification permission denied');
        return;
      }

      await PushNotifications.addListener('registration', (tok) => {
        console.log('Push token:', tok.value);
        sendTokenToBackend(tok.value);
      });

      await PushNotifications.addListener('registrationError', (err) => {
        console.error('Push registration error:', err.error);
      });

      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push received (foreground):', notification);
      });

      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const data = action.notification?.data;
        if (data?.route) {
          window.location.hash = data.route;
        }
      });

      await PushNotifications.register();
      registered.current = true;
    } catch (err) {
      console.error('Push notification initialization failed (non-fatal):', err);
      // Non-fatal: app continues without push notifications
    }
  }, [sendTokenToBackend]);

  useEffect(() => {
    if (token && Capacitor.isNativePlatform()) {
      // Small delay to let WebView stabilize after navigation
      const timer = setTimeout(() => initPush(), 1500);
      return () => clearTimeout(timer);
    }
    return () => {
      if (Capacitor.isNativePlatform()) {
        PushNotifications.removeAllListeners();
      }
    };
  }, [token, initPush]);
}
